/**
 * Hook to manage explicit links between QBO invoices and AppSheet service tickets.
 *
 * Manages the `invoice_service_links` Firestore collection.
 *
 * @example
 * // Fetch all links for one invoice
 * const { data: links } = useInvoiceServiceLinksForInvoice(invoiceDocId);
 *
 * // Fetch links for all invoices in the org (list view)
 * const { data: linksByInvoice } = useInvoiceServiceLinksByOrg(orgId);
 * const linksForInvoice = linksByInvoice?.get(invoiceDocId) ?? [];
 *
 * // Link an invoice to a service ticket
 * const { mutate: linkInvoice } = useLinkInvoiceToServiceTicket();
 * linkInvoice({ invoiceDocId, realmId, qboInvoiceId, docNumber, ... });
 *
 * // Remove a link
 * const { mutate: unlink } = useUnlinkInvoiceFromServiceTicket();
 * unlink({ linkId, invoiceDocId });
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { COLLECTIONS } from '../../config/collections';
import { linkedWorkKeys } from './linkedWorkKeys';
import type { InvoiceServiceLink, InvoiceServiceLinkMethod, InvoiceServiceSource } from '../../types/collections';
import toast from 'react-hot-toast';
import { logCallableFailure } from '../../lib/logCallableFailure';

// ============================================================================
// Query Key Factory
// ============================================================================

export const invoiceServiceLinkKeys = {
  all: ['invoiceServiceLinks'] as const,
  forInvoice: (invoiceDocId: string) =>
    [...invoiceServiceLinkKeys.all, 'forInvoice', invoiceDocId] as const,
  byOrg: (organizationId: string) =>
    [...invoiceServiceLinkKeys.all, 'byOrg', organizationId] as const,
  /** Held (`needs_review`) rows for the #3464 review queue. */
  needsReview: (organizationId: string) =>
    [...invoiceServiceLinkKeys.all, 'needsReview', organizationId] as const,
  needsReviewCount: (organizationId: string) =>
    [...invoiceServiceLinkKeys.all, 'needsReviewCount', organizationId] as const,
  /** Ticket realms + active siblings that the queue derives its warnings from. */
  reviewContext: (organizationId: string, ids: readonly string[]) =>
    [...invoiceServiceLinkKeys.all, 'reviewContext', organizationId, ids.length, ids[0] ?? '', ids[ids.length - 1] ?? ''] as const,
};

// ============================================================================
// Async Functions
// ============================================================================

/**
 * Client mirror of the backend `isActiveLinkStatus` whitelist
 * (`functions/src/field-service/services/readInvoiceServiceLinks.ts`).
 *
 * WHITELIST, never blacklist: a status this file has not heard of is NOT shown as
 * a confirmed link. `undefined`/`null` is the one grandfathered case — rows written
 * before US-010 carry no `status` at all and are real manual links.
 *
 * Two reasons this matters:
 *  - the backend P&L readers have been gated to `active` since #3080, but this
 *    display reader was not — so `needs_review` / `dismissed` rows render as if
 *    confirmed while P&L excludes them (#3092), and "linked" silently means two
 *    different things;
 *  - unlink is now a TOMBSTONE rather than a hard delete (#3460), so without this
 *    filter an unlinked pair would keep rendering as linked forever.
 */
export function isActiveInvoiceServiceLink(link: { status?: string | null; linkMethod?: string | null; dispositionSource?: string | null }): boolean {
  const status = link.status;
  // Same rule as the backend (#3094): a status-less row counts only when it is a legacy MANUAL link
  // whose marker is absent or human (an explicit 'machine' marker is never grandfathered, #3093).
  if (status === undefined || status === null) {
    return link.linkMethod === 'manual' && (link.dispositionSource === undefined || link.dispositionSource === 'human');
  }
  return status === 'active';
}

async function fetchLinksForInvoice(invoiceDocId: string, organizationId: string): Promise<InvoiceServiceLink[]> {
  const q = query(
    collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
    where('organizationId', '==', organizationId),
    where('invoiceDocId', '==', invoiceDocId),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }) as InvoiceServiceLink)
    .filter(isActiveInvoiceServiceLink);
}

async function fetchLinksByOrg(organizationId: string): Promise<Map<string, InvoiceServiceLink[]>> {
  const q = query(
    collection(db, COLLECTIONS.INVOICE_SERVICE_LINKS),
    where('organizationId', '==', organizationId),
  );
  const snapshot = await getDocs(q);

  const map = new Map<string, InvoiceServiceLink[]>();
  for (const d of snapshot.docs) {
    const link = { id: d.id, ...d.data() } as InvoiceServiceLink;
    if (!isActiveInvoiceServiceLink(link)) continue;
    const existing = map.get(link.invoiceDocId) ?? [];
    map.set(link.invoiceDocId, [...existing, link]);
  }
  return map;
}

// ============================================================================
// Input Types
// ============================================================================

export interface LinkInvoiceInput {
  invoiceDocId: string;
  realmId: string;
  qboInvoiceId: string;
  docNumber: string;
  serviceTicketId: string;
  appSheetServiceId: string;
  source: InvoiceServiceSource;
  customerName: string;
  organizationId: string;
  linkMethod?: InvoiceServiceLinkMethod;
}

export interface UnlinkInvoiceInput {
  linkId: string;
  invoiceDocId: string;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches all service ticket links for a single invoice.
 * Used on the invoice detail page.
 *
 * @param invoiceDocId - Firestore doc ID of the invoice (realmId_invoiceId)
 * @returns InvoiceServiceLink[]
 */
export function useInvoiceServiceLinksForInvoice(invoiceDocId?: string) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;
  return useQuery({
    queryKey: [...invoiceServiceLinkKeys.forInvoice(invoiceDocId ?? ''), organizationId],
    queryFn: () => fetchLinksForInvoice(invoiceDocId!, organizationId!),
    enabled: !!invoiceDocId && !!organizationId,
  });
}

/**
 * Batch fetch of invoice-service links for all invoices in an org.
 * Used for list views. Returns a Map keyed by invoiceDocId.
 *
 * @param organizationId - Organization ID to scope the query
 * @returns Map<string, InvoiceServiceLink[]> keyed by invoiceDocId
 */
export function useInvoiceServiceLinksByOrg(organizationId?: string) {
  return useQuery({
    queryKey: invoiceServiceLinkKeys.byOrg(organizationId ?? ''),
    queryFn: () => fetchLinksByOrg(organizationId!),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mutation to create a link between a QBO invoice and an AppSheet service ticket.
 * Includes optimistic updates: adds to cache immediately, rolls back on error.
 */
export function useLinkInvoiceToServiceTicket() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (input: LinkInvoiceInput) => {
      const callable = httpsCallable<
        LinkInvoiceInput & { linkedBy: string; linkedByName: string },
        { id: string } & Omit<InvoiceServiceLink, 'id'>
      >(functions, 'fieldService_linkInvoiceToServiceTicket');

      const result = await callable({
        ...input,
        linkedBy: user?.userId ?? '',
        linkedByName: user?.displayName ?? '',
      });

      return result.data as InvoiceServiceLink;
    },

    onMutate: async (input) => {
      const forInvoiceKey = invoiceServiceLinkKeys.forInvoice(input.invoiceDocId);
      const byOrgKey = invoiceServiceLinkKeys.byOrg(
        input.organizationId ?? currentOrganization?.organizationId ?? '',
      );

      await Promise.all([
        queryClient.cancelQueries({ queryKey: forInvoiceKey }),
        queryClient.cancelQueries({ queryKey: byOrgKey }),
      ]);

      const previousForInvoice = queryClient.getQueryData<InvoiceServiceLink[]>(forInvoiceKey);
      const previousByOrg = queryClient.getQueryData<Map<string, InvoiceServiceLink[]>>(byOrgKey);

      // Optimistic link with a temporary id
      const optimisticLink: InvoiceServiceLink = {
        id: `optimistic-${Date.now()}`,
        invoiceDocId: input.invoiceDocId,
        realmId: input.realmId,
        qboInvoiceId: input.qboInvoiceId,
        docNumber: input.docNumber,
        serviceTicketId: input.serviceTicketId,
        appSheetServiceId: input.appSheetServiceId,
        source: input.source,
        customerName: input.customerName,
        linkedBy: user?.userId ?? '',
        linkedByName: user?.displayName ?? '',
        linkedAt: Timestamp.now(),
        linkMethod: input.linkMethod ?? 'manual',
        organizationId: input.organizationId,
      };

      if (previousForInvoice) {
        queryClient.setQueryData<InvoiceServiceLink[]>(forInvoiceKey, [
          ...previousForInvoice,
          optimisticLink,
        ]);
      }

      if (previousByOrg) {
        const updatedMap = new Map(previousByOrg);
        const existing = updatedMap.get(input.invoiceDocId) ?? [];
        updatedMap.set(input.invoiceDocId, [...existing, optimisticLink]);
        queryClient.setQueryData<Map<string, InvoiceServiceLink[]>>(byOrgKey, updatedMap);
      }

      return { previousForInvoice, previousByOrg, forInvoiceKey, byOrgKey };
    },

    onError: (error: Error, _input, context) => {
      if (context?.previousForInvoice !== undefined) {
        queryClient.setQueryData(context.forInvoiceKey, context.previousForInvoice);
      }
      if (context?.previousByOrg !== undefined) {
        queryClient.setQueryData(context.byOrgKey, context.previousByOrg);
      }
      logCallableFailure('Failed to link invoice to service ticket:', error);
      toast.error('Failed to link service ticket');
    },

    onSettled: (_data, _error, input, context) => {
      if (context) {
        queryClient.invalidateQueries({ queryKey: context.forInvoiceKey });
        queryClient.invalidateQueries({ queryKey: context.byOrgKey });
      } else {
        queryClient.invalidateQueries({ queryKey: invoiceServiceLinkKeys.all });
      }
      // The Collect rows' / To Send cards' per-invoice linked-work chips (L-1263).
      queryClient.invalidateQueries({ queryKey: linkedWorkKeys.forInvoiceAll() });
    },

    onSuccess: () => {
      toast.success('Service ticket linked');
    },
  });
}

/**
 * Mutation to hard-delete a link between a QBO invoice and an AppSheet service ticket.
 * Includes optimistic updates: removes from cache immediately, rolls back on error.
 */
export function useUnlinkInvoiceFromServiceTicket() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';

  return useMutation({
    mutationFn: async (input: UnlinkInvoiceInput) => {
      const callable = httpsCallable<
        { linkId: string },
        { success: boolean }
      >(functions, 'fieldService_unlinkInvoiceFromServiceTicket');

      await callable({ linkId: input.linkId });
    },

    onMutate: async (input) => {
      const forInvoiceKey = invoiceServiceLinkKeys.forInvoice(input.invoiceDocId);
      const byOrgKey = invoiceServiceLinkKeys.byOrg(orgId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: forInvoiceKey }),
        queryClient.cancelQueries({ queryKey: byOrgKey }),
      ]);

      const previousForInvoice = queryClient.getQueryData<InvoiceServiceLink[]>(forInvoiceKey);
      const previousByOrg = queryClient.getQueryData<Map<string, InvoiceServiceLink[]>>(byOrgKey);

      if (previousForInvoice) {
        queryClient.setQueryData<InvoiceServiceLink[]>(
          forInvoiceKey,
          previousForInvoice.filter((link) => link.id !== input.linkId),
        );
      }

      if (previousByOrg) {
        const updatedMap = new Map(previousByOrg);
        const existing = updatedMap.get(input.invoiceDocId) ?? [];
        updatedMap.set(
          input.invoiceDocId,
          existing.filter((link) => link.id !== input.linkId),
        );
        queryClient.setQueryData<Map<string, InvoiceServiceLink[]>>(byOrgKey, updatedMap);
      }

      return { previousForInvoice, previousByOrg, forInvoiceKey, byOrgKey };
    },

    onError: (error: Error, _input, context) => {
      if (context?.previousForInvoice !== undefined) {
        queryClient.setQueryData(context.forInvoiceKey, context.previousForInvoice);
      }
      if (context?.previousByOrg !== undefined) {
        queryClient.setQueryData(context.byOrgKey, context.previousByOrg);
      }
      logCallableFailure('Failed to unlink invoice from service ticket:', error);
      toast.error('Failed to remove service ticket link');
    },

    onSettled: (_data, _error, _input, context) => {
      if (context) {
        queryClient.invalidateQueries({ queryKey: context.forInvoiceKey });
        queryClient.invalidateQueries({ queryKey: context.byOrgKey });
      } else {
        queryClient.invalidateQueries({ queryKey: invoiceServiceLinkKeys.all });
      }
      // The Collect rows' / To Send cards' per-invoice linked-work chips (L-1263).
      queryClient.invalidateQueries({ queryKey: linkedWorkKeys.forInvoiceAll() });
    },

    onSuccess: () => {
      toast.success('Service ticket link removed');
    },
  });
}
